import { useState, useEffect } from 'react';
import { useRollbar } from '@rollbar/react'
import isEqual from 'lodash/isEqual';

import useUserManagement from './useUserManagement';
import useAuthentication from './useAuthentication';

const CACHE_SECONDS = 15;
const LOCAL_STORAGE_KEY = 'projects';

const useProjectList = () => {
    const rollbar = useRollbar()
    const { currentUser } = useAuthentication();
    
    const localStorageData = localStorage.getItem(LOCAL_STORAGE_KEY) ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) : {};
    const [lastUpdated, _setLastUpdated] = useState(localStorageData?.lastUpdated || 0);
    const [projects, _setProjects] = useState(localStorageData?.projects || []);

    useEffect(() => {
        const shouldUpdateCache = lastUpdated + (1000 * CACHE_SECONDS) < Date.now();

        if (projects.length === 0 || shouldUpdateCache) {
            reloadProjects();
        }
    }, [])

    const reloadProjects = () => {
        fetch('/v0/projects', { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) {
                    return response.text().then((bodyText) => {
                        const error = new Error(`HTTP error ${response.status}`);
                        error.response = response;
                        error.body = bodyText;
                        throw error;
                    })
                }
                return response.json()
            })
            .then(function(json) {
                processProjects(json.results)
            })
            .catch(error => {
                if (error?.response?.status === 403) {
                    // Just ignore this, they don't have access
                    return
                }
                rollbar?.error?.("useProjectList.reloadProjects error", error, {
                    currentUserId: currentUser?.id,
                    status: error?.response?.status,
                    body: error?.body,
                })
            })
    }

    const _processProject = (row) => ({
        id: row.id,
        name: row.name,
        bid_manager_user_id: row.bid_manager_user_id,
        project_manager_user_id: row.project_manager_user_id,
        project_code: row.project_code,
        project_status_name: row.project_status_name || row.project_status.name,
        project_type_name: row.project_type_name || row.project_type.name,
        description: row.description,
        is_archived: row.is_archived,
        customer: row.customer,
    });
    
    const processProjects = (input) => {
        const data = input.map((row) => _processProject(row))
        _setLastUpdated(Date.now())
        if( ! isEqual(data, projects) ) {
            _setProjects(data)
        }
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
            lastUpdated: Date.now(),
            projects: data,
        }))
    }

    // For updating local cache, so it's deleted straight away without waiting for update list from server
    const projectDelete = (id) => {
        const updated = projects.filter(item => item.id !== id)
        _setProjects(updated)
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
            lastUpdated: Date.now(),
            projects: updated,
        }))
    }

    const _projectUpdate = (id, projectData) => {
        const exists = projects.some(item => item.id === id)
        if ( exists ) {
            console.log("_projectUpdate: Project already exists")
            const updated = projects.map(item => {
                if( item.id === id ) {
                    item = _processProject({ ...item, ...projectData })
                }
                return item
            })
            return updated
        }
        // else
        const newProject = _processProject(projectData)
        console.log("_projectUpdate: new Project", newProject)
        return [ ...projects, newProject ]
    }

    // For updating the local cache so we don't have to wait for the server update
    const projectUpdate = (id, projectData) => {
        const updated = _projectUpdate(id, projectData)
        _setProjects(updated)
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
            lastUpdated: Date.now(),
            projects: updated,
        }))
    }
    
    const getProjectFromID = (id) => {
        const project = projects.find(item => item.id === id)
        return project // might be undefined
    }

    return {
        projects,
        projectDelete,
        projectUpdate,
        reloadProjects,
        getProjectFromID,
    }
}

export default useProjectList
